import { useState } from 'react';
import {
    Card,
    CardContent,
    Stack,
    Badge,
    Avatar,
    Box,
    TextField,
    Chip,
    IconButton,
    Tooltip,
    Checkbox,
    Button,
    Typography,
    Divider,
    useTheme,
    alpha,
    Grid,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    ArrowDropDown as ArrowDropDownIcon,
    Cancel as CancelIcon,
    Check as CheckIcon,
    CheckCircle as CheckCircleIcon,
    DarkMode as DarkModeIcon,
    Delete as DeleteIcon,
    Email as EmailIcon,
    ExpandMore as ExpandMoreIcon,
    LightMode as LightModeIcon,
    PanTool as AssistantIcon,
    Person as MemberIcon,
    PersonAddAlt as PersonAddIcon,
    RemoveCircleOutlined as RemoveIcon,
    Schedule as ScheduleIcon,
    Shield as ShieldIcon,
    SupervisorAccount as AdminIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    VpnKey as KeyIcon,
    Event as EventIcon,
    EventNote as TemplateSetIcon,
    BugReport as BugReportIcon,
} from '@mui/icons-material';
import { themeConstants, styleHelpers } from '@/theme';
import { useI18nContext } from '@/i18n/i18n-react';
import type { AdminResource, Category } from '@/types';
import CategoryIcon from '@/components/CategoryIcon.tsx';

interface Resource {
    id: number;
    name: string;
    mail: string | null;
    auth: string;
    active: number;
    system: number;
    admin: number;
    canEdit: boolean;
    scheduleEditor: number;
    scheduleTemplateEditor: number;
    eventEditor: number;
    debug: number;
    login: string | null;
    theme?: 'dark' | 'light';
    categories: string[];
    proxies: string[];
}

interface UserCardProps {
    user: Resource;
    isExpanded: boolean;
    onExpand: () => void;
    onUpdateUser: (userId: number, field: string, value: any) => void;
    onDeleteUser: (userId: number) => void;
    onCategory: (
        userId: number,
        categoryId: number,
        state: 'member' | 'assistant' | 'none'
    ) => void;
    onResponsible: (userId: number, categoryId: number, responsible: boolean) => void;
    onDeleteProxy: (userId: number, proxyId: number) => void;
    onAddProxy: () => void;
    categories: Category[];
    categoryResources: Record<number, { assistant: boolean }>;
    allResources: AdminResource[];
    authCodeEnabled?: boolean;
    scheduleEnabled?: boolean;
    oidcAdminGroup?: string | null;
}

export const UserCard = ({
    user,
    isExpanded,
    onExpand,
    onUpdateUser,
    onDeleteUser,
    onCategory,
    onResponsible,
    onDeleteProxy,
    onAddProxy,
    categories,
    categoryResources,
    allResources,
    authCodeEnabled = true,
    scheduleEnabled = false,
    oidcAdminGroup,
}: UserCardProps) => {
    const theme = useTheme();
    const { LL } = useI18nContext();
    const [showPassword, setShowPassword] = useState(false);
    const [categoryMenuAnchor, setCategoryMenuAnchor] = useState<{
        element: HTMLElement;
        categoryId: number;
    } | null>(null);

    const [rolesMenuAnchorEl, setRolesMenuAnchorEl] = useState<HTMLElement | null>(null);

    const roleToggles = [
        {
            key: 'active' as const,
            label: user.active === 1 ? LL.common.active() : LL.common.inactive(),
            tooltip: user.active === 1 ? LL.userCard.activeUser() : LL.common.inactive(),
            checked: user.active === 1,
            disabled: user.system === 1,
            onChange: (checked: boolean) => onUpdateUser(user.id, 'active', checked ? 1 : 0),
            icon: <CancelIcon fontSize="small" />,
            checkedIcon: <CheckCircleIcon fontSize="small" />,
            color: (checked: boolean) => (checked ? 'success.main' : 'error.main'),
        },
        {
            key: 'admin' as const,
            label: LL.userCard.admin(),
            tooltip: oidcAdminGroup
                ? LL.userCard.adminManagedByOidc()
                : LL.userCard.administrator(),
            checked: user.admin === 1,
            disabled: user.system === 1 || !!oidcAdminGroup,
            onChange: (checked: boolean) => onUpdateUser(user.id, 'admin', checked ? 1 : 0),
            icon: <MemberIcon fontSize="small" />,
            checkedIcon: <AdminIcon fontSize="small" />,
            color: (_checked: boolean) => 'warning.main',
        },
        ...(scheduleEnabled
            ? [
                  {
                      key: 'scheduleEditor' as const,
                      label: LL.userCard.scheduleEditor(),
                      tooltip: LL.userCard.scheduleEditor(),
                      checked: user.scheduleEditor === 1,
                      disabled: user.system === 1,
                      onChange: (checked: boolean) =>
                          onUpdateUser(user.id, 'scheduleEditor', checked ? 1 : 0),
                      icon: <ScheduleIcon fontSize="small" />,
                      checkedIcon: <ScheduleIcon fontSize="small" />,
                      color: (_checked: boolean) => 'info.main',
                  },
                  {
                      key: 'scheduleTemplateEditor' as const,
                      label: LL.userCard.scheduleTemplateEditor(),
                      tooltip: LL.userCard.scheduleTemplateEditor(),
                      checked: user.scheduleTemplateEditor === 1,
                      disabled: user.system === 1,
                      onChange: (checked: boolean) =>
                          onUpdateUser(user.id, 'scheduleTemplateEditor', checked ? 1 : 0),
                      icon: <TemplateSetIcon fontSize="small" />,
                      checkedIcon: <TemplateSetIcon fontSize="small" />,
                      color: (_checked: boolean) => 'secondary.main',
                  },
              ]
            : []),
        {
            key: 'eventEditor' as const,
            label: LL.userCard.eventEditor(),
            tooltip: LL.userCard.eventEditor(),
            checked: user.eventEditor === 1,
            disabled: user.system === 1,
            onChange: (checked: boolean) => onUpdateUser(user.id, 'eventEditor', checked ? 1 : 0),
            icon: <EventIcon fontSize="small" />,
            checkedIcon: <EventIcon fontSize="small" />,
            color: (_checked: boolean) => 'error.main',
        },
        {
            key: 'debug' as const,
            label: LL.userCard.debug(),
            tooltip: LL.userCard.debugTooltip(),
            checked: user.debug === 1,
            disabled: user.system === 1,
            onChange: (checked: boolean) => onUpdateUser(user.id, 'debug', checked ? 1 : 0),
            icon: <BugReportIcon fontSize="small" />,
            checkedIcon: <BugReportIcon fontSize="small" />,
            color: (_checked: boolean) => 'info.main',
        },
    ];

    return (
        <Card
            sx={{
                opacity: user.active === 0 ? 0.6 : 1,
                transition: themeConstants.transitions.smooth,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                '&:hover': {
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                    boxShadow: themeConstants.effects.boxShadowGlow,
                },
            }}
        >
            <CardContent>
                <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                    {/* Avatar & Name */}
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
                            <Badge
                                overlap="circular"
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                badgeContent={
                                    user.active === 1 ? (
                                        <CheckCircleIcon sx={styleHelpers.statusBadge(true)} />
                                    ) : (
                                        <CancelIcon sx={styleHelpers.statusBadge(false)} />
                                    )
                                }
                            >
                                <Avatar sx={styleHelpers.roleAvatar(user.admin === 1)}>
                                    {user.admin === 1 ? (
                                        <AdminIcon fontSize="large" />
                                    ) : (
                                        <MemberIcon fontSize="large" />
                                    )}
                                </Avatar>
                            </Badge>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={user.name}
                                    disabled={!user.canEdit}
                                    onChange={(e) => onUpdateUser(user.id, 'name', e.target.value)}
                                    slotProps={{
                                        input: {
                                            sx: { fontWeight: 600, fontSize: '1.1rem' },
                                        },
                                    }}
                                />
                            </Box>
                        </Stack>
                    </Grid>

                    {/* Email */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                            <EmailIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                            <TextField
                                size="small"
                                fullWidth
                                type="email"
                                disabled={!user.canEdit || user.system === 1}
                                placeholder={LL.userCard.emailPlaceholder()}
                                value={user.mail || ''}
                                onChange={(e) => onUpdateUser(user.id, 'mail', e.target.value)}
                            />
                        </Stack>
                    </Grid>

                    {/* Auth PIN - only show if auth code is enabled */}
                    {authCodeEnabled && (
                        <Grid size={{ xs: 12, md: 2 }}>
                            <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                                <KeyIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                <TextField
                                    size="small"
                                    fullWidth
                                    type={showPassword ? 'text' : 'password'}
                                    value={user.auth}
                                    onChange={(e) =>
                                        onUpdateUser(user.id, 'auth', e.target.value.toUpperCase())
                                    }
                                    slotProps={{ htmlInput: { maxLength: 4 } }}
                                    placeholder={LL.userCard.pinPlaceholder()}
                                />
                                <Tooltip
                                    title={
                                        showPassword ? LL.userCard.hidePin() : LL.userCard.showPin()
                                    }
                                >
                                    <IconButton
                                        size="small"
                                        onMouseDown={() => setShowPassword(true)}
                                        onMouseUp={() => setShowPassword(false)}
                                        onMouseLeave={() => setShowPassword(false)}
                                        sx={{
                                            '&:hover': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.15),
                                            },
                                        }}
                                    >
                                        {showPassword ? (
                                            <VisibilityIcon fontSize="small" />
                                        ) : (
                                            <VisibilityOffIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Grid>
                    )}

                    {/* Roles + Actions (cluster right on md+) */}
                    <Grid size={{ xs: 12, md: authCodeEnabled ? 3 : 5 }}>
                        <Stack
                            direction="row"
                            sx={{
                                gap: 1,
                                justifyContent: { xs: 'space-between', md: 'flex-end' },
                                alignItems: 'center',
                                flexWrap: 'wrap',
                            }}
                        >
                            {/* Desktop/tablet: icon-only toggles with tooltips */}
                            <Stack
                                direction="row"
                                sx={{
                                    gap: 0.5,
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    display: { xs: 'none', sm: 'flex' },
                                    justifyContent: { md: 'flex-end' },
                                }}
                            >
                                {roleToggles.map((t) => (
                                    <Tooltip key={t.key} title={t.tooltip}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={t.disabled}
                                                onClick={() => t.onChange(!t.checked)}
                                                sx={{
                                                    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                                    bgcolor: t.checked
                                                        ? alpha(theme.palette.primary.main, 0.08)
                                                        : 'transparent',
                                                    color: t.checked
                                                        ? t.color(true)
                                                        : 'text.secondary',
                                                    '&:hover': {
                                                        bgcolor: alpha(
                                                            theme.palette.primary.main,
                                                            0.12
                                                        ),
                                                    },
                                                }}
                                            >
                                                {t.checked ? t.checkedIcon : t.icon}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                ))}
                            </Stack>

                            {/* Mobile: single menu button with labels */}
                            <Stack
                                direction="row"
                                sx={{
                                    justifyContent: 'flex-start',
                                    display: { xs: 'flex', sm: 'none' },
                                }}
                            >
                                <Button
                                    size="small"
                                    variant="outlined"
                                    endIcon={<ArrowDropDownIcon />}
                                    onClick={(e) => setRolesMenuAnchorEl(e.currentTarget)}
                                    sx={{
                                        textTransform: 'none',
                                        px: 1.25,
                                    }}
                                >
                                    {LL.userCard.roles()}
                                </Button>
                                <Menu
                                    anchorEl={rolesMenuAnchorEl}
                                    open={Boolean(rolesMenuAnchorEl)}
                                    onClose={() => setRolesMenuAnchorEl(null)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                minWidth: 240,
                                                mt: 0.5,
                                            },
                                        },
                                    }}
                                >
                                    {roleToggles.map((t) => {
                                        const checkedColor = t.color(true);
                                        const uncheckedColor = theme.palette.text.secondary;

                                        return (
                                            <MenuItem
                                                key={t.key}
                                                disabled={t.disabled}
                                                onClick={() => {
                                                    t.onChange(!t.checked);
                                                }}
                                                sx={{
                                                    '& .MuiListItemIcon-root': {
                                                        minWidth: 36,
                                                    },
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={{
                                                        color: t.checked
                                                            ? checkedColor
                                                            : uncheckedColor,
                                                        opacity: t.disabled ? 0.5 : 1,
                                                    }}
                                                >
                                                    {t.checked ? t.checkedIcon : t.icon}
                                                </ListItemIcon>
                                                <ListItemText>{t.label}</ListItemText>
                                                <Checkbox
                                                    edge="end"
                                                    checked={t.checked}
                                                    tabIndex={-1}
                                                    disableRipple
                                                    disabled={t.disabled}
                                                    sx={{
                                                        ml: 1,
                                                        color: t.checked
                                                            ? checkedColor
                                                            : uncheckedColor,
                                                        '&.Mui-checked': {
                                                            color: checkedColor,
                                                        },
                                                    }}
                                                />
                                            </MenuItem>
                                        );
                                    })}
                                </Menu>
                            </Stack>

                            {/* Actions */}
                            <Stack direction="row" sx={{ gap: 1, justifyContent: 'flex-end' }}>
                                <Tooltip
                                    title={
                                        isExpanded
                                            ? LL.userCard.hideDetails()
                                            : LL.userCard.showDetails()
                                    }
                                >
                                    <Button
                                        size="small"
                                        variant={isExpanded ? 'contained' : 'outlined'}
                                        onClick={onExpand}
                                        sx={{ minWidth: 'auto', px: 1.5 }}
                                    >
                                        <ExpandMoreIcon
                                            sx={{
                                                transform: isExpanded ? 'rotate(180deg)' : 'none',
                                                transition: 'transform 0.3s',
                                            }}
                                        />
                                    </Button>
                                </Tooltip>
                                <Tooltip title={LL.userCard.deleteUser()}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        disabled={user.system === 1}
                                        onClick={() => onDeleteUser(user.id)}
                                        sx={{ minWidth: 'auto', px: 1.5 }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </Button>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    </Grid>
                </Grid>

                <Stack
                    direction="row"
                    sx={{
                        mt: 2,
                        pt: 2,
                        gap: 1.5,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                    }}
                >
                    {/* Theme Indicator */}
                    <Tooltip
                        title={
                            user.theme === 'light'
                                ? LL.userCard.themeLight()
                                : LL.userCard.themeDark()
                        }
                    >
                        {user.theme === 'light' ? (
                            <LightModeIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                        ) : (
                            <DarkModeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        )}
                    </Tooltip>

                    {/* Last Login Info */}
                    <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                        <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                            }}
                        >
                            {LL.userCard.lastLogin()}: {user.login ?? LL.userCard.never()}
                        </Typography>
                    </Stack>

                    {user.admin === 1 && (
                        <Chip
                            icon={<ShieldIcon sx={{ fontSize: '0.75rem' }} />}
                            label={LL.userCard.admin()}
                            size="small"
                            variant="outlined"
                            color="default"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                    )}

                    {/* Category Statistics */}
                    {(() => {
                        const memberCount = Object.keys(categoryResources).filter(
                            (catId) => !categoryResources[parseInt(catId)]?.assistant
                        ).length;
                        const assistantCount = Object.keys(categoryResources).filter(
                            (catId) => categoryResources[parseInt(catId)]?.assistant
                        ).length;
                        const responsibleCount = user.categories.length;

                        return (
                            <>
                                {memberCount > 0 && (
                                    <Chip
                                        icon={<MemberIcon sx={{ fontSize: '0.875rem' }} />}
                                        label={`${LL.userCard.member()}: ${memberCount}`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.7rem', userSelect: 'none' }}
                                    />
                                )}
                                {assistantCount > 0 && (
                                    <Chip
                                        icon={<AssistantIcon sx={{ fontSize: '0.75rem' }} />}
                                        label={`${LL.userCard.assistant()}: ${assistantCount}`}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.7rem', userSelect: 'none' }}
                                    />
                                )}
                                {responsibleCount > 0 && (
                                    <Chip
                                        icon={<AdminIcon sx={{ fontSize: '0.875rem' }} />}
                                        label={`${LL.userCard.responsible()}: ${responsibleCount}`}
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.7rem', userSelect: 'none' }}
                                    />
                                )}
                            </>
                        );
                    })()}
                </Stack>

                {/* Expanded Details */}
                {isExpanded && (
                    <Box
                        sx={{
                            mt: 3,
                            pt: 3,
                            borderTop: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        }}
                    >
                        {/* Categories Section */}
                        <Box sx={{ mb: 3 }}>
                            <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                                        color: 'primary.main',
                                    }}
                                >
                                    <ScheduleIcon fontSize="small" />
                                </Avatar>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                    }}
                                >
                                    {LL.userCard.categories()}
                                </Typography>
                            </Stack>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'text.secondary',
                                    mb: 2,
                                    display: 'block',
                                }}
                            >
                                {LL.userCard.categoryHelperText()}
                            </Typography>
                            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                                {categories?.map((category) => {
                                    const isInCategory = !!categoryResources[category.id];
                                    const isAssistant = categoryResources[category.id]?.assistant;
                                    const isResponsible = user.categories.includes(
                                        category.id.toString()
                                    );

                                    // Determine base membership status and color
                                    let statusIcon = null;
                                    let chipColor: 'default' | 'primary' | 'secondary' | 'warning' =
                                        'default';
                                    let chipVariant: 'outlined' | 'filled' = 'outlined';

                                    // Responsible status takes precedence for color
                                    if (isResponsible) {
                                        chipColor = 'warning';
                                        chipVariant = 'filled';
                                    } else if (isInCategory) {
                                        chipColor = isAssistant ? 'secondary' : 'primary';
                                        chipVariant = 'filled';
                                    }

                                    // Build icon based on combination of states
                                    if (isResponsible && isInCategory) {
                                        // Show both icons when user is responsible AND a member/helper
                                        statusIcon = (
                                            <Stack
                                                direction="row"
                                                sx={{ gap: 0.25, alignItems: 'center' }}
                                            >
                                                <AdminIcon sx={{ fontSize: 16 }} />
                                                {isAssistant ? (
                                                    <AssistantIcon sx={{ fontSize: 16 }} />
                                                ) : (
                                                    <CheckIcon sx={{ fontSize: 16 }} />
                                                )}
                                            </Stack>
                                        );
                                    } else if (isResponsible) {
                                        // Only responsible (not a member)
                                        statusIcon = <AdminIcon sx={{ fontSize: 18 }} />;
                                    } else if (isInCategory) {
                                        // Only member/helper (not responsible)
                                        if (isAssistant) {
                                            statusIcon = <AssistantIcon sx={{ fontSize: 18 }} />;
                                        } else {
                                            statusIcon = <CheckIcon sx={{ fontSize: 18 }} />;
                                        }
                                    }

                                    return (
                                        <Chip
                                            key={category.id}
                                            label={
                                                <Stack
                                                    direction="row"
                                                    sx={{ gap: 0.5, alignItems: 'center' }}
                                                >
                                                    <CategoryIcon
                                                        size="18px"
                                                        category={category.icon}
                                                    />
                                                    <Typography>{category.name}</Typography>
                                                </Stack>
                                            }
                                            icon={statusIcon || undefined}
                                            deleteIcon={
                                                <ArrowDropDownIcon
                                                    sx={{
                                                        '&:hover': {
                                                            color: 'inherit !important',
                                                        },
                                                    }}
                                                />
                                            }
                                            onDelete={(e) =>
                                                setCategoryMenuAnchor({
                                                    element: e.currentTarget,
                                                    categoryId: category.id,
                                                })
                                            }
                                            color={chipColor}
                                            variant={chipVariant}
                                            sx={{
                                                fontWeight: isResponsible ? 700 : 600,
                                                transition: 'all 0.2s',
                                                boxShadow: isResponsible
                                                    ? `0 0 8px ${alpha(theme.palette.warning.main, 0.3)}`
                                                    : undefined,
                                                '& .MuiChip-deleteIcon': {
                                                    color: 'inherit',
                                                    opacity: 0.7,
                                                    transition: 'opacity 0.2s',
                                                    '&:hover': {
                                                        opacity: 1,
                                                    },
                                                },
                                            }}
                                        />
                                    );
                                })}
                            </Stack>

                            {/* Category Action Menu */}
                            <Menu
                                anchorEl={categoryMenuAnchor?.element}
                                open={Boolean(categoryMenuAnchor)}
                                onClose={() => setCategoryMenuAnchor(null)}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                slotProps={{
                                    paper: {
                                        sx: {
                                            minWidth: 220,
                                            mt: 0.5,
                                        },
                                    },
                                }}
                            >
                                {categoryMenuAnchor &&
                                    (() => {
                                        const categoryId = categoryMenuAnchor.categoryId;
                                        const isInCategory = !!categoryResources[categoryId];
                                        const isAssistant =
                                            categoryResources[categoryId]?.assistant;
                                        const isResponsible = user.categories.includes(
                                            categoryId.toString()
                                        );

                                        return (
                                            <>
                                                <MenuItem
                                                    onClick={() => {
                                                        // Set as member (not helper)
                                                        // Keep responsible status unchanged
                                                        if (!isInCategory || isAssistant) {
                                                            onCategory(
                                                                user.id,
                                                                categoryId,
                                                                'member'
                                                            );
                                                        }
                                                        setCategoryMenuAnchor(null);
                                                    }}
                                                    disabled={isInCategory && !isAssistant}
                                                    sx={{
                                                        bgcolor:
                                                            isInCategory && !isAssistant
                                                                ? alpha(
                                                                      theme.palette.primary.main,
                                                                      0.1
                                                                  )
                                                                : undefined,
                                                    }}
                                                >
                                                    <ListItemIcon>
                                                        <MemberIcon
                                                            fontSize="small"
                                                            color="primary"
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText>
                                                        {LL.userCard.member()}
                                                    </ListItemText>
                                                    {isInCategory && !isAssistant && (
                                                        <CheckIcon
                                                            fontSize="small"
                                                            color="primary"
                                                        />
                                                    )}
                                                </MenuItem>

                                                <MenuItem
                                                    onClick={() => {
                                                        // Set as helper (assistant)
                                                        // Keep responsible status unchanged
                                                        if (!isInCategory || !isAssistant) {
                                                            onCategory(
                                                                user.id,
                                                                categoryId,
                                                                'assistant'
                                                            );
                                                        }
                                                        setCategoryMenuAnchor(null);
                                                    }}
                                                    disabled={isInCategory && isAssistant}
                                                    sx={{
                                                        bgcolor:
                                                            isInCategory && isAssistant
                                                                ? alpha(
                                                                      theme.palette.secondary.main,
                                                                      0.1
                                                                  )
                                                                : undefined,
                                                    }}
                                                >
                                                    <ListItemIcon>
                                                        <AssistantIcon
                                                            fontSize="small"
                                                            color="secondary"
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText>
                                                        {LL.userCard.assistant()}
                                                    </ListItemText>
                                                    {isInCategory && isAssistant && (
                                                        <CheckIcon
                                                            fontSize="small"
                                                            color="secondary"
                                                        />
                                                    )}
                                                </MenuItem>
                                                <MenuItem
                                                    onClick={() => {
                                                        // Remove membership but keep responsible
                                                        if (isInCategory) {
                                                            onCategory(user.id, categoryId, 'none');
                                                        }
                                                        setCategoryMenuAnchor(null);
                                                    }}
                                                    disabled={!isInCategory}
                                                    sx={{
                                                        color: !isInCategory
                                                            ? 'text.disabled'
                                                            : 'text.primary',
                                                    }}
                                                >
                                                    <ListItemIcon>
                                                        <RemoveIcon
                                                            fontSize="small"
                                                            sx={{
                                                                color: !isInCategory
                                                                    ? 'text.disabled'
                                                                    : 'text.secondary',
                                                            }}
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText>
                                                        {LL.userCard.removeMembership()}
                                                    </ListItemText>
                                                </MenuItem>

                                                <Divider sx={{ my: 1 }} />

                                                <MenuItem
                                                    onClick={() => {
                                                        onResponsible(
                                                            user.id,
                                                            categoryId,
                                                            !isResponsible
                                                        );
                                                        setCategoryMenuAnchor(null);
                                                    }}
                                                    sx={{
                                                        bgcolor: isResponsible
                                                            ? alpha(theme.palette.warning.main, 0.1)
                                                            : undefined,
                                                    }}
                                                >
                                                    <ListItemIcon>
                                                        <AdminIcon
                                                            fontSize="small"
                                                            sx={{
                                                                color: isResponsible
                                                                    ? 'warning.main'
                                                                    : 'text.secondary',
                                                            }}
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText>
                                                        {isResponsible
                                                            ? `✓ ${LL.userCard.responsible()}`
                                                            : LL.userCard.responsible()}
                                                    </ListItemText>
                                                </MenuItem>
                                            </>
                                        );
                                    })()}
                            </Menu>
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Proxies Section */}
                        <Box>
                            <Stack direction="row" sx={{ gap: 1, alignItems: 'center', mb: 2 }}>
                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: alpha(theme.palette.info.main, 0.2),
                                        color: 'info.main',
                                    }}
                                >
                                    <PersonAddIcon fontSize="small" />
                                </Avatar>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                    }}
                                >
                                    {LL.userCard.proxies()}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<PersonAddIcon />}
                                    onClick={onAddProxy}
                                    sx={{ ml: 'auto !important' }}
                                >
                                    {LL.userCard.addProxy()}
                                </Button>
                            </Stack>
                            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                                {user.proxies.length === 0 ? (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {LL.userCard.noProxies()}
                                    </Typography>
                                ) : (
                                    user.proxies.map((proxyId) => {
                                        const proxyUser = allResources.find(
                                            (r) => r.id === parseInt(proxyId)
                                        );
                                        return (
                                            <Chip
                                                key={proxyId}
                                                avatar={
                                                    <Avatar sx={{ bgcolor: 'info.main' }}>
                                                        <MemberIcon fontSize="small" />
                                                    </Avatar>
                                                }
                                                label={
                                                    proxyUser?.name ||
                                                    LL.userCard.unknownUser({
                                                        id: parseInt(proxyId),
                                                    })
                                                }
                                                onDelete={() =>
                                                    onDeleteProxy(user.id, parseInt(proxyId))
                                                }
                                                color="info"
                                                variant="outlined"
                                                sx={{
                                                    fontWeight: 600,
                                                    '& .MuiChip-deleteIcon:hover': {
                                                        color: 'error.main',
                                                    },
                                                }}
                                            />
                                        );
                                    })
                                )}
                            </Stack>
                        </Box>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};
